import { useEffect, useRef } from "react";
import { useQuery } from "seed/gql";
import { usePost } from "seed/api";
import { ControllerActions, ControllerEvents, ControllerType } from "components/util/controller";
import { HOST_SHELL_PATHS } from "components/util/host_routes";

export const useAppControllerActions = ({
  flow,
  dispatchToApp,
  registerChat,
  unregisterChat,
  updateChatProps,
  registerMenu,
  unregisterMenu,
  updateMenuProps,
  setMenuOpen,
  resolveChatApi,
  registerComponentRequest,
  takeComponentRequest,
  takeScreenshot,
  getAppStorage,
  setAppStorage,
}) => {

  // Processes asked for before the root node lookup resolved. The app mounts and can run a
  // process on its first effect — well before this query answers — so without this the
  // action would be dropped and its callback would never fire.
  const queuedExecutionsRef = useRef([]);

  const executionSessionId = sessionStorage.getItem("execution_session_id");

  const reqRootNodes = useQuery(`
    {
      flowNodes {
        id
        rootName
        inputValues
      }
    }
  `, `flow.id=${flow.id} AND is_root=true AND is_active=true`);

  const [callExecute] = usePost("/flows/execute_node");

  // Answering a component resumes the node that is sitting AWAITING on it — the same
  // endpoint a form submit uses, because to the execution engine this is that.
  const [callSendForm] = usePost("/apps/forms/send_form");

  const rootNodes = reqRootNodes.data?.flowNodes;
  const isEngineReady = rootNodes != null || reqRootNodes.error != null;

  const findRootNodeByProcessName = (processName) => {
    // The app executes processes by public process name. Subflows expose that name
    // through inputValues, so both rootName and subflow name are valid.
    if (!processName) return null;
    return rootNodes?.find(node => node.rootName === processName) || null;
  }

  // Translates the vocabulary the app writes (processName / nodeId / params) into the props
  // Chat takes. Keeping the mapping here means the controller API that project code
  // depends on stays stable regardless of how Chat names things internally.
  const resolveChatProps = (chatProps) => {

    if (!chatProps) return chatProps;

    const { processName, nodeId, params, ...rest } = chatProps;

    // The name travels as a name: the backend resolves it across the flow's inheritance
    // chain, so the app can name a process defined in a parent flow, whose nodes it never
    // loaded and whose ids mean nothing here.
    return {
      ...rest,
      ...(processName != null ? { autoRunProcessName: processName } : {}),
      ...(nodeId != null ? { autoRunNodeId: nodeId } : {}),
      ...(params != null ? { executionVariables: params } : {}),
    };
  }

  const runProcess = (message) => {

    const rootNode = findRootNodeByProcessName(message.processName);
    if (!rootNode) return;

    callExecute({
      root_node_id: rootNode.id,
      flow_id: flow.id,
      session_id: executionSessionId,
      web_execution_id: message._seed_execution_id,
      variables: message.payload || {},
      is_view_execution: true,
    });

  }

  const handleExecuteProcess = (message) => {

    if (!isEngineReady) {
      queuedExecutionsRef.current.push(message);
      return;
    }

    runProcess(message);

  }

  useEffect(() => {
    // Drain in arrival order once the lookup resolves. Runs on the render where
    // isEngineReady flips, so runProcess already sees the loaded nodes.
    if (!isEngineReady) return;
    const queued = queuedExecutionsRef.current;
    if (!queued.length) return;
    queuedExecutionsRef.current = [];
    queued.forEach(runProcess);
  }, [isEngineReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildNavigationPath = (path, params = {}) => {
    // Params used in /detail/:id become path segments; the rest become query string values
    // so navigate supports both router params and filters.
    if (!isValidPath(path)) return null;

    const usedParams = new Set();
    const resolvedPath = path.trim().replace(/:([A-Za-z0-9_]+)/g, (_, key) => {
      usedParams.add(key);
      return encodeURIComponent(params?.[key] ?? "");
    });

    const queryParams = Object.entries(params || {})
      .filter(([key, value]) => !usedParams.has(key) && value != null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    const query = new URLSearchParams(queryParams).toString();
    return query ? `${resolvedPath}?${query}` : resolvedPath;
  }

  // The host's side menu asking the app to route somewhere. The app owns its router, so this
  // is forwarded rather than applied: the host never resolves a project path itself.
  const handleNavigate = (message) => {

    const rawPath = typeof message.path === "string" ? message.path.trim() : "";
    if (isAppShellPath(rawPath)) {
      window.location.assign(rawPath);
      return;
    }

    const nextPath = buildNavigationPath(message.path, {
      ...(message.params || message.parameters || {}),
      ...(message.chatId ? { chat_id: message.chatId } : {}),
    });

    if (nextPath)
      dispatchToApp({
        type: ControllerType.EVENT,
        name: ControllerEvents.OnNavigate,
        path: nextPath,
      });

  }

  const handleCleanMessages = (message) =>
    console.log("CleanMessages controller action disabled", { message });

  const handleRegisterChat = (message) => registerChat(message.slotId, message.chatProps);

  const handleUnregisterChat = (message) => unregisterChat(message.slotId);

  const handleUpdateChatProps = (message) => updateChatProps(message.slotId, message.chatProps);

  const handleRegisterMenu = (message) => registerMenu(message.slotId, message.menuProps);

  const handleUnregisterMenu = (message) => unregisterMenu(message.slotId);

  const handleUpdateMenuProps = (message) => updateMenuProps(message.slotId, message.menuProps);

  const handleSetMenuOpen = (message) => setMenuOpen(message.slotId, message.isOpen);

  const handleTakeScreenshot = (message) =>
    takeScreenshot({ requestId: message.requestId, options: message.options });

  const handleSetAppStorage = (message) => setAppStorage(message.storage || message.patch || {});

  const handleRequestAppStorage = () =>
    dispatchToApp({
      type: ControllerType.EVENT,
      name: ControllerEvents.OnAppStorageChanged,
      storage: getAppStorage(),
    });

  // ─── Messages the app writes ───

  // The app sending on the user's behalf: a JSON payload the process reads by key, files
  // already uploaded, or a message that never shows up in the thread.
  const handleSendChatMessage = (message) => {

    const chatApi = resolveChatApi(message.slotId);
    if (!chatApi) {
      console.warn("[Seed:Controller] No chat to send to", { slotId: message.slotId });
      return;
    }

    chatApi.sendMessage({
      message: message.message ?? "",
      attachments: message.attachments || [],
      visible: message.visible !== false,
    });

  }

  // ─── UI tools ───

  // The chat received a SHOW_COMPONENT over the websocket. The ids it carries stay here;
  // the app is told only what it needs to paint the thing.
  const handleShowComponent = (message) => {

    registerComponentRequest({
      requestId: message.requestId,
      componentName: message.componentName,
      slotId: message.slotId,
      chatId: message.chatId,
      nodeId: message.nodeId,
      executionId: message.executionId,
      messageId: message.messageId,
      sessionId: message.sessionId,
      token: message.token,
    });

    dispatchToApp({
      type: ControllerType.EVENT,
      name: ControllerEvents.OnShowComponent,
      requestId: message.requestId,
      componentName: message.componentName,
      props: message.props || {},
    });

  }

  // The run that asked for the component is over — it finished, failed or was stopped.
  // It has to clear either way, or the screen keeps asking for something nobody is
  // listening for any more.
  const handleHideComponent = (message) => {

    if (message.requestId) takeComponentRequest(message.requestId);

    dispatchToApp({
      type: ControllerType.EVENT,
      name: ControllerEvents.OnHideComponent,
      requestId: message.requestId,
    });

  }

  const answerComponent = (requestId, response) => {

    const request = takeComponentRequest(requestId);
    if (!request) {
      // Answered twice, or answered after the run already moved on. Dropping it is
      // right: the node is no longer waiting, and sending would resume nothing.
      console.warn("[Seed:Controller] No component waiting for this answer", { requestId });
      return;
    }

    callSendForm({
      response: { component: request.componentName, request_id: requestId, ...response },
      sender_id: sessionStorage.getItem("id"),
      node_id: request.nodeId,
      execution_id: request.executionId,
      message_id: request.messageId,
      session_id: request.sessionId,
      token: request.token,
    });

    dispatchToApp({
      type: ControllerType.EVENT,
      name: ControllerEvents.OnHideComponent,
      requestId,
    });

  }

  const handleSubmitComponent = (message) =>
    answerComponent(message.requestId, { data: message.data ?? {} });

  const handleCancelComponent = (message) =>
    answerComponent(message.requestId, { cancelled: true, reason: message.reason || "" });

  const handlers = {
    [ControllerActions.Navigate]: handleNavigate,
    [ControllerActions.ExecuteProcess]: handleExecuteProcess,
    [ControllerActions.CleanMessages]: handleCleanMessages,
    [ControllerActions.RegisterChat]: handleRegisterChat,
    [ControllerActions.UnregisterChat]: handleUnregisterChat,
    [ControllerActions.UpdateChatProps]: handleUpdateChatProps,
    [ControllerActions.RegisterMenu]: handleRegisterMenu,
    [ControllerActions.UnregisterMenu]: handleUnregisterMenu,
    [ControllerActions.UpdateMenuProps]: handleUpdateMenuProps,
    [ControllerActions.SetMenuOpen]: handleSetMenuOpen,
    [ControllerActions.TakeScreenshot]: handleTakeScreenshot,
    [ControllerActions.SetAppStorage]: handleSetAppStorage,
    [ControllerActions.RequestAppStorage]: handleRequestAppStorage,
    [ControllerActions.SendChatMessage]: handleSendChatMessage,
    [ControllerActions.ShowComponent]: handleShowComponent,
    [ControllerActions.HideComponent]: handleHideComponent,
    [ControllerActions.SubmitComponent]: handleSubmitComponent,
    [ControllerActions.CancelComponent]: handleCancelComponent,
  };

  return { handlers, resolveChatProps };

}

const isValidPath = (path) =>
  typeof path === "string" &&
  path.trim() &&
  !["undefined", "null"].includes(path.trim().toLowerCase());

// Navigating to an app shell route must replace the top window so the whole app reboots.
const isAppShellPath = (path) =>
  typeof path === "string" &&
  HOST_SHELL_PATHS.some(base => path === base || path.startsWith(`${base}?`) || path.startsWith(`${base}/`));
